import { FormEvent, useState } from 'react';
import { api } from '../lib/api';
import { classNames } from '../lib/format';
import type { Product } from '../types';

export function BulkPanel({ products, reload }: { products: Product[]; reload: () => Promise<void> }) {
  const [mode, setMode] = useState<'percent' | 'fixed'>('percent');
  const [direction, setDirection] = useState<'down' | 'up'>('down');
  const [value, setValue] = useState(10);
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const categories = [...new Set(products.flatMap((p) => p.categories))].sort();

  async function apply(event: FormEvent) {
    event.preventDefault();
    if (!value) {
      setMessage({ tone: 'error', text: 'Indica un valor' });
      return;
    }
    const where = category ? `categoría "${category}"` : 'todos los productos';
    const arrow = direction === 'down' ? 'bajar' : 'subir';
    const unit = mode === 'percent' ? '%' : ' CLP';
    if (!window.confirm(`¿Confirmas ${arrow} los precios de ${where} en ${value}${unit}? Esto modifica todos los precios de inmediato.`)) return;

    setBusy(true);
    setMessage(null);
    try {
      const result = await api.admin.bulkPriceUpdate({ mode, value, direction, category: category || undefined });
      setMessage({ tone: 'ok', text: `✓ ${result.affected} producto${result.affected === 1 ? '' : 's'} actualizado${result.affected === 1 ? '' : 's'}.` });
      await reload();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-card">
      <div className="admin-card-head">
        <div>
          <h2>Cambio masivo de precios</h2>
          <p className="muted">Útil para sales/outlet o ajustes por temporada. Aplica a todos los productos no archivados.</p>
        </div>
        <button type="button" className="ghost-button compact" onClick={() => api.admin.exportProducts()}>Export CSV</button>
      </div>

      <form onSubmit={apply} className="form-grid" style={{ marginTop: 14 }}>
        <label>
          Acción
          <select value={direction} onChange={(e) => setDirection(e.target.value as 'down' | 'up')}>
            <option value="down">Bajar precios</option>
            <option value="up">Subir precios</option>
          </select>
        </label>
        <label>
          Tipo
          <select value={mode} onChange={(e) => setMode(e.target.value as 'percent' | 'fixed')}>
            <option value="percent">Porcentaje (%)</option>
            <option value="fixed">Monto fijo (CLP)</option>
          </select>
        </label>
        <label>
          Valor
          <input type="number" min={0} value={value} onChange={(e) => setValue(Number(e.target.value))} />
        </label>
        <label>
          Aplicar a
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Todos los productos</option>
            {categories.map((c) => (
              <option key={c} value={c}>Categoría: {c}</option>
            ))}
          </select>
        </label>
        <div className="span-2">
          <button className="primary-button" disabled={busy}>{busy ? 'Aplicando...' : 'Aplicar cambio'}</button>
        </div>
      </form>

      {message && <p className={classNames('form-message', `tone-${message.tone}`)} style={{ marginTop: 12 }}>{message.text}</p>}
    </section>
  );
}
